// Components/Forms/Fields/Controls/SelectControl.tsx
import React from 'react';
import { Form } from 'react-bootstrap';
import { useController, useFormContext } from 'react-hook-form';
import { LoadingControl } from '../LoadingControl';
import type { SelectOption } from '@app/Utilities/SelectMap';

type SelectControlProps<TValue extends string | number> = {
  name: string;
  options: Array<SelectOption<TValue>>;
  loading?: boolean;
  placeholder?: string;
  className?: string;

  /**
   * Optional parse function. If not supplied, the component will infer
   * TValue based on the first option's value type.
   */
  parse?: (raw: string) => TValue;

  /**
   * Optional: override invalid state (rarely needed).
   * By default this comes from RHF fieldState.
   */
  isInvalid?: boolean;

  /** Optional: disabled state */
  disabled?: boolean;
};

export const SelectControl = <TValue extends string | number>({
  name,
  options,
  loading,
  placeholder,
  className,
  parse,
  isInvalid,
  disabled,
}: SelectControlProps<TValue>) => {
  const { control } = useFormContext();
  const { field, fieldState } = useController({ name: name as any, control });

  const coerce = (raw: string): TValue | undefined => {
    // "" is usually a placeholder selection
    if (raw === '') return undefined;

    // Explicit parsing always wins
    if (parse) return parse(raw);

    // Infer from the first option's value
    const inferredType = typeof options[0]?.value;

    if (inferredType === 'number') return Number(raw) as TValue;
    if (inferredType === 'string') return String(raw) as TValue;

    return raw as TValue;
  };

  if (loading) return <LoadingControl />;

  return (
    <Form.Select
      className={className}
      disabled={disabled}
      isInvalid={isInvalid ?? fieldState.invalid}
      value={field.value ?? ''}
      onChange={(e) => field.onChange(coerce(e.target.value))}
      onBlur={field.onBlur}
      name={field.name}
      ref={field.ref}
    >
      {placeholder && <option value="">{placeholder}</option>}
      {options.map((o) => (
        <option key={String(o.value)} value={String(o.value)}>
          {o.label}
        </option>
      ))}
    </Form.Select>
  );
};
